import { HbrIcon } from '@harbor/elements-react-standalone';
import { caretDown } from '@harbor/elements/icons';
import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import {
  useCreateApprovalHistoryMutation,
  useUpdateApprovalRequestMutation,
} from '../../../hooks/approval-request-hooks';
import {
  useSyncEscalationJiraTicket,
  useUpdateEscalationMutation,
} from '../../../hooks/escalation-request-hooks';
import { RootState } from '../../../support/store';
import { formatDateTime } from '../../../support/utils';
import {
  ApprovalRequest,
  ApprovalRequestPayload,
  ApprovalRequestSource,
  ApprovalRequestStatusLevel,
  EscalationLevel,
  EscalationRequest,
  EscalationRequestAssignment,
  RequestHistoryPayload,
} from '../../../types/approval-request';
import { Product } from '../../../types/product';
import StbApiModal from '../../security-toolbox-ui/StbApiModal';
import StbButton from '../../security-toolbox-ui/StbButton';
import {
  getAssignmentDisplayText,
  getJiraAssigneeLabel,
  getJiraExternalTicket,
  getJiraIssueKey,
  getJiraIssueUrl,
  getJiraRequestTypeLabel,
  getJiraStatusLabel,
  getJiraTicketHistory,
  getRequestorEscalationLevelActions,
  getNextAvailableEscalationLevel
} from './opportunityEscalationHelpers';
import OpportunityEscalationModal from './OpportunityEscalationModal';
import BuEscalationModal from './BuEscalationModal';


type Props = {
  id?: string;
  level: EscalationLevel;
  approval?: ApprovalRequest | null;
  assignment?: EscalationRequestAssignment | null;
  escalation: EscalationRequest;
  product: Product;
};

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value?: React.ReactNode;
}) => {
  if (!value) return null;

  return (
    <div className="grid grid-cols-[8rem_minmax(0,1fr)] gap-2">
      <div className="font-semibold text-gray-600">{label}</div>
      <div className="min-w-0 break-words">{value}</div>
    </div>
  );
};

type ActiveModal = 'none' | 'cancel' | 'update-escalate' | 'escalate-bu';

const OpportunityEscalationListRequestStage: React.FC<Props> = ({
  level,
  approval,
  assignment,
  escalation,
  product,
}) => {
  const { id: userId } = useSelector((state: RootState) => state.user.userData);
  const { id: opportunityId } = useParams();

  const [activeModal, setActiveModal] = useState<ActiveModal>('none');
  const [expanded, setExpanded] = useState(false);

  const requestObjectIds = {
    [ApprovalRequestSource.Opportunity]: opportunityId,
  };
  const { mutateAsync: createApprovalHistory } = useCreateApprovalHistoryMutation();
  const { mutateAsync: updateApprovalRequest } = useUpdateApprovalRequestMutation(requestObjectIds);
  const { mutateAsync: updateEscalationtMutation } = useUpdateEscalationMutation(opportunityId);

  const stage = useMemo(() => {
    switch (level) {
      case EscalationLevel.Domain:
        return 'Domain Specialist';
      case EscalationLevel.Incubation:
        return 'Incubation Team';
      case EscalationLevel.Business:
        return 'Business Unit';
      default:
        return `Level ${level}`;
    }
  }, [level]);

  const detailText = approval?.justification ||
    approval?.approver_note ||
    assignment?.notes ||
    'No justification provided.';

  const isBusinessUnitStage = Number(level) === EscalationLevel.Business;

  // A BU escalation is worked in Jira, so keep its ticket state fresh while this row is on
  // screen - otherwise the row would keep showing the status the ticket had when it was filed.
  const { data: syncedEscalation } = useSyncEscalationJiraTicket(opportunityId, escalation?.id, {
    enabled: isBusinessUnitStage && !!assignment,
  });

  // Prefer the synced escalation: the list response does not always carry external tickets, so
  // the escalation prop can be ticket-less even when the issue exists in Jira.
  const jiraTicket = useMemo(
    () => getJiraExternalTicket(syncedEscalation) ?? getJiraExternalTicket(escalation),
    [syncedEscalation, escalation]
  );

  const jiraIssueKey = getJiraIssueKey(jiraTicket, escalation);
  const jiraIssueUrl = getJiraIssueUrl(jiraTicket, escalation);
  const jiraStatus = jiraTicket ? getJiraStatusLabel(jiraTicket) : null;
  const jiraAssignee = getJiraAssigneeLabel(jiraTicket);
  const jiraRequestType = getJiraRequestTypeLabel(jiraTicket);

  // The worklog, so anyone looking at the escalation can see what the BU has done without
  // needing the portal. Only the synced escalation carries comments; the list response omits them.
  const jiraHistory = useMemo(() => getJiraTicketHistory(jiraTicket), [jiraTicket]);

  const assignmentDisplayText = useMemo(
    () => getAssignmentDisplayText({ level, assignment, approval, escalation, jiraTicket }),
    [level, assignment, approval, escalation, jiraTicket]
  );

  const requestorEmail = approval?.requestor?.email || approval?.requestor_email || assignment?.requested_by?.email || '-';

  const requestedAt = useMemo(
    () => formatDateTime(approval?.created || assignment?.created || null),
    [approval, assignment]
  );

  const approverEmail = approval?.approver?.email || approval?.approver_email || null;
  const decisionAt = approval?.decision_timestamp ? formatDateTime(approval.decision_timestamp) : null;
  const assignedToEmail = assignment?.assigned_to?.email ?? null;

  const targetEscalation = useMemo(() => {
    return getNextAvailableEscalationLevel(escalation);
  }, [escalation]);

  const { canUpdate, canCancel, canEscalate } = getRequestorEscalationLevelActions({
    userId,
    approval,
    assignment,
    escalation,
  });

  const handleOpenCancelModal = useCallback(() => {
    setActiveModal('cancel');
  }, []);

  const handleOpenUpdateEscalateModal = useCallback(() => {
    setActiveModal('update-escalate');
  }, []);

  const handleToggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  const handleUpdateClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      handleOpenUpdateEscalateModal();
    },
    [handleOpenUpdateEscalateModal]
  );

  const handleCancelClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      handleOpenCancelModal();
    },
    [handleOpenCancelModal]
  );

  const handleEscalateClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      // Route to appropriate modal based on target escalation level
      if (targetEscalation?.targetLevel === EscalationLevel.Business) {
        setActiveModal('escalate-bu');
      } else {
        setActiveModal('update-escalate');
      }
    },
    [targetEscalation]
  );

  const handleCancelRequest = async () => {
    if (!approval) return;

    const status = ApprovalRequestStatusLevel.Cancelled;

    const payload: Omit<ApprovalRequestPayload, 'requested_item_id'> = {
      id: approval?.id,
      requestor_id: approval?.requestor?.id,
      approver_id: approval?.approver?.id,
      requested_item_type: approval?.requested_item_type,
      status_id: status,
    };

    await updateApprovalRequest(payload);

    const history: RequestHistoryPayload = {
      approval_request_id: approval?.id,
      previous_status_id: approval?.status?.id ?? null,
      current_status_id: status,
      action_by_user_id: approval?.requestor?.id,
    };

    await createApprovalHistory(history);

    if (escalation.stage === EscalationLevel.Domain) {
      const escalationUpdate = {
        id: escalation?.id,
        description: escalation?.description ?? '',
        status_id: status,
      };

      await updateEscalationtMutation(escalationUpdate);
    }
  };

  const updateModalOpen = activeModal === 'update-escalate';
  const cancelModalOpen = activeModal === 'cancel';
  const buEscalationModalOpen = activeModal === 'escalate-bu';

  if (!approval && !assignment) return null;

  return (
    <>
      <div className="border-t border-[#eee]">
        <div
          className="flex flex-row justify-between items-center py-1 cursor-pointer"
          onClick={handleToggleExpanded}
        >
          <div className="font-medium">{stage}</div>

          <div className="flex flex-row gap-2 items-center">
            {assignmentDisplayText && (
              <div className="text-gray-500">{assignmentDisplayText}</div>
            )}

            {canUpdate && (
              <StbButton
                role="update"
                size="xsmall"
                variant="text"
                onClick={handleUpdateClick}
              >
                Update
              </StbButton>
            )}

            {canCancel && (
              <StbButton
                role="delete"
                size="xsmall"
                variant="text"
                onClick={handleCancelClick}
              >
                Cancel
              </StbButton>
            )}

            {canEscalate && (
              <StbButton
                role="request"
                size="xsmall"
                variant="text"
                onClick={handleEscalateClick}
              >
                Escalate
              </StbButton>
            )}

            <HbrIcon
              asset={caretDown}
              className={`duration-300 ${
                expanded ? '' : '-rotate-90'
              } transition-all ml-2`}
            />
          </div>
        </div>

        <div className={`${expanded ? '' : 'hidden'}`}>
          <div className="bg-[#f9f9f9] px-2 py-2 text-sm text-gray-700 flex flex-col gap-2">
            <div>
              <div className="font-bold mb-1">Justification</div>
              <div className="whitespace-pre-wrap">{detailText}</div>
            </div>

            <div className="border-t border-gray-200 pt-2 flex flex-col gap-1">
              <DetailRow label="Requestor" value={requestorEmail} />
              <DetailRow label="Requested" value={requestedAt} />

              {isBusinessUnitStage && (
                <>
                  <DetailRow label="Request Type" value={jiraRequestType} />

                  <DetailRow
                    label="JIRA Id"
                    value={
                      jiraIssueUrl ? (
                        <a
                          href={jiraIssueUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#0d6ca4] underline hover:no-underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {jiraIssueKey}
                        </a>
                      ) : (
                        jiraIssueKey ?? '-'
                      )
                    }
                  />

                  <DetailRow label="JIRA Status" value={jiraStatus} />
                  <DetailRow label="JIRA Assignee" value={jiraAssignee} />
                </>
              )}

              {assignedToEmail && (
                <DetailRow label="Assigned To" value={assignedToEmail} />
              )}

              {approverEmail && (
                <DetailRow label="Approver" value={approverEmail} />
              )}

              {approval?.decision_timestamp && (
                <DetailRow label="Decision" value={decisionAt} />
              )}
            </div>

            {isBusinessUnitStage && (
              <div className="border-t border-gray-200 pt-2">
                <div className="font-bold mb-1">Ticket History</div>

                {jiraHistory.length ? (
                  <ol className="flex flex-col gap-2">
                    {jiraHistory.map((entry) => (
                      <li key={entry.id} className="flex flex-col">
                        <div className="flex flex-row flex-wrap gap-2 text-xs text-gray-500">
                          <span className="font-semibold text-gray-600">
                            {entry.author ?? 'Unknown'}
                          </span>
                          {entry.created && <span>{formatDateTime(entry.created)}</span>}
                        </div>
                        <div className="whitespace-pre-wrap break-words">{entry.body}</div>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <div className="text-gray-500">No worklog entries yet.</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {(canUpdate || canEscalate) && targetEscalation?.targetLevel !== EscalationLevel.Business && (
        <OpportunityEscalationModal
          open={updateModalOpen}
          setOpen={(open) => setActiveModal(open ? 'update-escalate' : 'none')}
          request={approval}
          requestProduct={product}
          requestEscalation={escalation}
          isEscalating={canEscalate}
          targetEscalationLevel={targetEscalation?.targetLevel}
          targetAssignmentTypeId={targetEscalation?.targetAssignmentTypeId}
        />
      )}

      {canEscalate && targetEscalation?.targetLevel === EscalationLevel.Business && (
        <BuEscalationModal
          open={buEscalationModalOpen}
          onClose={() => setActiveModal('none')}
          escalation={escalation}
        />
      )}

      {canCancel && (
        <StbApiModal
          open={cancelModalOpen}
          role="delete"
          submitLabel="Cancel Request"
          submitSuccess="Request cancelled"
          onSubmit={handleCancelRequest}
          onClose={async () => setActiveModal('none')}
        >
          <div>Are you sure you want to cancel this request?</div>
        </StbApiModal>
      )}
    </>
  );
};

export default OpportunityEscalationListRequestStage;